import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { SiteContentProvider } from "./contexts/SiteContent";
import { CustomerAuthProvider, CurrencyProvider, CartProvider } from "./shop";

// Public landing components (eagerly loaded)
import Nav from "./components/Nav";
import Hero from "./components/Hero";
import FeaturedRow from "./components/FeaturedRow";
import WhyScience from "./components/WhyScience";
import AnchorTabs from "./components/AnchorTabs";
import Domains from "./components/Domains";
import Breakthroughs from "./components/Breakthroughs";
import Impact from "./components/Impact";
import CtaBanner from "./components/CtaBanner";
import Resources from "./components/Resources";
import SocialNewsletter from "./components/SocialNewsletter";
import Footer from "./components/Footer";
import ContactModal from "./components/ContactModal";

// Phase 4c: detail pages (eagerly loaded — small and reuse landing chrome)
import CardDetail from "./pages/CardDetail";
import NotFound from "./pages/NotFound";

// Admin section is code-split so it doesn't bloat the landing bundle.
const AuthProvider = lazy(() =>
  import("./admin/AuthContext").then((m) => ({ default: m.AuthProvider }))
);
const RequireAuth = lazy(() => import("./admin/RequireAuth"));
const AdminLayout = lazy(() => import("./admin/Layout"));
const AdminLogin = lazy(() => import("./admin/pages/Login"));
const Dashboard = lazy(() => import("./admin/pages/Dashboard"));
const CardsList = lazy(() => import("./admin/pages/CardsList"));
const CardEditor = lazy(() => import("./admin/pages/CardEditor"));
const SiteContent = lazy(() => import("./admin/pages/SiteContent"));
const Newsletter = lazy(() => import("./admin/pages/Newsletter"));
const Contact = lazy(() => import("./admin/pages/Contact"));

// Admin shop pages (sub-chunked together — all loaded as one chunk via AdminShop module)
const AdminShopProducts = lazy(() =>
  import("./admin/pages/AdminShop").then((m) => ({ default: m.AdminShopProducts }))
);
const AdminProductEditor = lazy(() =>
  import("./admin/pages/AdminShop").then((m) => ({ default: m.AdminProductEditor }))
);
const AdminOrders = lazy(() =>
  import("./admin/pages/AdminShop").then((m) => ({ default: m.AdminOrders }))
);
const AdminOrderDetail = lazy(() =>
  import("./admin/pages/AdminShop").then((m) => ({ default: m.AdminOrderDetail }))
);
const AdminDiscountCodes = lazy(() =>
  import("./admin/pages/AdminShop").then((m) => ({ default: m.AdminDiscountCodes }))
);
const AdminTaxRegions = lazy(() =>
  import("./admin/pages/AdminShop").then((m) => ({ default: m.AdminTaxRegions }))
);
const AdminShippingRates = lazy(() =>
  import("./admin/pages/AdminShop").then((m) => ({ default: m.AdminShippingRates }))
);
const AdminShopSettings = lazy(() =>
  import("./admin/pages/AdminShop").then((m) => ({ default: m.AdminSettings }))
);
const AdminCustomers = lazy(() =>
  import("./admin/pages/AdminShop").then((m) => ({ default: m.AdminCustomers }))
);

// Public shop & customer (own chunk)
const ShopList = lazy(() => import("./pages/shop/ShopList"));
const ShopDetail = lazy(() => import("./pages/shop/ShopDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CustomerLogin = lazy(() => import("./pages/account/Login"));
const Account = lazy(() => import("./pages/account/Account"));
const OrdersList = lazy(() =>
  import("./pages/orders/Orders").then((m) => ({ default: m.OrdersList }))
);
const OrderDetail = lazy(() =>
  import("./pages/orders/Orders").then((m) => ({ default: m.OrderDetail }))
);

function PublicLanding() {
  const [contactOpen, setContactOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!target) return;
    const t = setTimeout(() => {
      const id = String(target).replace(/^#/, "");
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else if (id === "top") window.scrollTo({ top: 0, behavior: "smooth" });
    }, 80);
    return () => clearTimeout(t);
  }, [location.key, location.state]);

  return (
    <div className="min-h-screen bg-white text-neutral-900 antialiased">
      <Nav />
      <main>
        <Hero />
        <FeaturedRow />
        <WhyScience />
        <AnchorTabs />
        <Domains />
        <Breakthroughs />
        <Impact />
        <CtaBanner />
        <Resources />
        <SocialNewsletter />
      </main>
      <Footer onOpenContact={() => setContactOpen(true)} />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}

const SuspenseFallback = ({ label = "Loading…" }) => (
  <div className="min-h-screen grid place-items-center text-neutral-500 text-sm">{label}</div>
);

function AdminRoutes() {
  return (
    <Suspense fallback={<SuspenseFallback label="Loading admin…" />}>
      <AuthProvider>
        <Routes>
          <Route path="login" element={<AdminLogin />} />
          <Route
            element={
              <RequireAuth>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="cards" element={<CardsList />} />
            <Route path="cards/new" element={<CardEditor />} />
            <Route path="cards/:id" element={<CardEditor />} />
            <Route path="site-content" element={<SiteContent />} />
            <Route path="newsletter" element={<Newsletter />} />
            <Route path="contact" element={<Contact />} />
            {/* Shop sub-pages */}
            <Route path="shop/products" element={<AdminShopProducts />} />
            <Route path="shop/products/new" element={<AdminProductEditor />} />
            <Route path="shop/products/:id" element={<AdminProductEditor />} />
            <Route path="shop/orders" element={<AdminOrders />} />
            <Route path="shop/orders/:id" element={<AdminOrderDetail />} />
            <Route path="shop/discount-codes" element={<AdminDiscountCodes />} />
            <Route path="shop/tax-regions" element={<AdminTaxRegions />} />
            <Route path="shop/shipping-rates" element={<AdminShippingRates />} />
            <Route path="shop/settings" element={<AdminShopSettings />} />
            <Route path="shop/customers" element={<AdminCustomers />} />
          </Route>
        </Routes>
      </AuthProvider>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" closeButton />
        <SiteContentProvider>
          <CustomerAuthProvider>
            <CurrencyProvider>
              <CartProvider>
                <Routes>
                  <Route path="/" element={<PublicLanding />} />
                  <Route path="/featured/:slug" element={<CardDetail section="featured" />} />
                  <Route path="/domains/:slug" element={<CardDetail section="domains" />} />
                  <Route path="/breakthroughs/:slug" element={<CardDetail section="breakthroughs" />} />
                  <Route path="/impact/:slug" element={<CardDetail section="impact" />} />
                  <Route path="/resources/:slug" element={<CardDetail section="resources" />} />

                  {/* Shop & customer routes (code-split) */}
                  <Route
                    path="/shop"
                    element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <ShopList />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/shop/:slug"
                    element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <ShopDetail />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/cart"
                    element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <Cart />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/checkout"
                    element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <Checkout />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/account/login"
                    element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <CustomerLogin />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/account"
                    element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <Account />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/orders"
                    element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <OrdersList />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/orders/:id"
                    element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <OrderDetail />
                      </Suspense>
                    }
                  />

                  <Route path="/admin/*" element={<AdminRoutes />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </CartProvider>
            </CurrencyProvider>
          </CustomerAuthProvider>
        </SiteContentProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
